import { Col, Divider, Row, Typography } from 'antd';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CHART_COLORS, getDropShadowFilter } from '../../../constants/styles';
import { DeltaIndicator } from '../DeltaIndicator';

const { Text } = Typography;

interface IChartDataItem {
  category: string;
  expected: number;
  actual: number;
}

interface IExpectedVsActualChartProps {
  data: IChartDataItem[];
  maxBarWidth?: number;
  isCompact?: boolean;
  /** Comparison data for inline compare mode - overlaid bars */
  comparisonData?: IChartDataItem[];
  /** Current date range label for legend (inline mode) */
  currentDateRangeLabel?: string;
  /** Comparison date range label for legend (inline mode) */
  comparisonDateRangeLabel?: string;
  /** Subtitle to display above the bars */
  subtitle?: string;
  /** Pane width for responsive scaling */
  paneWidth?: number;
  /** Number of pending donations excluded from expected totals */
  pendingDonations?: number;
  /** Whether in split screen mode */
  isSplitMode?: boolean;
  /** Previous period data for trend arrows (only in normal/default mode) */
  previousPeriodData?: IChartDataItem[];
}

// Color scheme for each category: [expected (light), actual (dark)]
// Using brand colors
const CATEGORY_COLORS: Record<string, [string, string]> = {
  Total: [CHART_COLORS.Total.expected, CHART_COLORS.Total.actual],
  'Total SGL': [
    CHART_COLORS['Total SGL'].expected,
    CHART_COLORS['Total SGL'].actual,
  ],
  'Total DBL': [
    CHART_COLORS['Total DBL'].expected,
    CHART_COLORS['Total DBL'].actual,
  ],
  pRBC: [CHART_COLORS.pRBC.expected, CHART_COLORS.pRBC.actual],
  'pRBC SGL': [
    CHART_COLORS['pRBC SGL'].expected,
    CHART_COLORS['pRBC SGL'].actual,
  ],
  'pRBC DBL': [
    CHART_COLORS['pRBC DBL'].expected,
    CHART_COLORS['pRBC DBL'].actual,
  ],
  FFP: [CHART_COLORS.FFP.expected, CHART_COLORS.FFP.actual],
  'FFP SGL': [CHART_COLORS['FFP SGL'].expected, CHART_COLORS['FFP SGL'].actual],
  'FFP DBL': [CHART_COLORS['FFP DBL'].expected, CHART_COLORS['FFP DBL'].actual],
};

// Sub-row categories that should be visually indented
const SUB_CATEGORIES = new Set([
  'Total SGL',
  'Total DBL',
  'pRBC SGL',
  'pRBC DBL',
  'FFP SGL',
  'FFP DBL',
]);

// Main categories for legend display (excludes sub-categories)
const LEGEND_COLORS: [string, string][] = [
  CATEGORY_COLORS.Total,
  CATEGORY_COLORS.pRBC,
  CATEGORY_COLORS.FFP,
];

// Text colors for expected values (light for dark background)
const EXPECTED_TEXT_COLORS: Record<string, string> = {
  Total: '#cedbef', // Pale blue-gray
  'Total SGL': '#b8c5d9', // Lighter gray-blue
  'Total DBL': '#cedbef', // Pale blue-gray
  pRBC: '#fca5a5', // Light red
  'pRBC SGL': '#fecaca', // Very light red
  'pRBC DBL': '#fca5a5', // Light red
  FFP: '#93c5fd', // Light blue
  'FFP SGL': '#bfdbfe', // Very light blue
  'FFP DBL': '#93c5fd', // Light blue
};

// Text colors for actual values (bright/white for dark background)
const ACTUAL_TEXT_COLORS: Record<string, string> = {
  Total: '#ffffff', // White
  'Total SGL': '#e5e7eb', // Light gray
  'Total DBL': '#f3f4f6', // Near-white
  pRBC: '#fca5a5', // Light red
  'pRBC SGL': '#fecaca', // Very light red
  'pRBC DBL': '#fca5a5', // Light red
  FFP: '#93c5fd', // Light blue
  'FFP SGL': '#bfdbfe', // Very light blue
  'FFP DBL': '#93c5fd', // Light blue
};

// Get colors for a category (fallback to default if not found)
function getCategoryColors(category: string): [string, string] {
  return CATEGORY_COLORS[category] || ['#d9d9d9', '#595959'];
}

// Get text color for expected value (falls back to bar color if not specified)
function getExpectedTextColor(category: string): string {
  return (
    EXPECTED_TEXT_COLORS[category] ||
    CATEGORY_COLORS[category]?.[0] ||
    '#cedbef'
  );
}

// Get text color for actual value (falls back to white if not specified)
function getActualTextColor(category: string): string {
  return (
    ACTUAL_TEXT_COLORS[category] || CATEGORY_COLORS[category]?.[1] || '#ffffff'
  );
}

export const ExpectedVsActualChart: FC<IExpectedVsActualChartProps> = ({
  data,
  maxBarWidth,
  isCompact = false,
  comparisonData,
  currentDateRangeLabel,
  comparisonDateRangeLabel,
  subtitle,
  paneWidth,
  pendingDonations = 0,
  isSplitMode = false,
  previousPeriodData,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Calculate responsive scaling factor for spacing
  // Scale from 1.0 at 800px+ down to 0.25 at 280px (very aggressive for narrow split panes)
  const getScaleFactor = () => {
    if (!paneWidth || paneWidth >= 800) return 1.0;
    if (paneWidth <= 280) return 0.25;
    // Linear interpolation between 280px (0.25) and 800px (1.0)
    return 0.25 + ((paneWidth - 280) / (800 - 280)) * 0.75;
  };
  const scaleFactor = getScaleFactor();

  // Responsive column widths - scale with pane width
  // Very aggressive minimums for narrow split panes
  const labelColWidth = isCompact
    ? 40
    : Math.max(36, Math.round(70 * scaleFactor));
  const valuesColWidth = isCompact
    ? 55
    : Math.max(50, Math.round(100 * scaleFactor));
  const circleColWidth = comparisonData
    ? isCompact
      ? 45
      : Math.max(40, Math.round(80 * scaleFactor))
    : isCompact
    ? 32
    : Math.max(32, Math.round(60 * scaleFactor));
  const circleMarginLeft = isCompact
    ? 1
    : Math.max(1, Math.round(6 * scaleFactor)) + 70;

  // Responsive spacing values - very tight for narrow panes
  const outerPadding = isCompact ? 2 : Math.max(2, Math.round(6 * scaleFactor));
  const legendGap = isCompact ? 6 : Math.max(4, Math.round(12 * scaleFactor));
  const legendMarginBottom = isCompact
    ? 4
    : Math.max(4, Math.round(10 * scaleFactor));
  const rowGutter = isCompact ? 2 : Math.max(2, Math.round(6 * scaleFactor));

  if (data.length === 0) {
    return (
      <div
        style={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ color: '#a7b4c2' }}>No data</Text>
      </div>
    );
  }

  // Find max value for scaling (include comparison data if present)
  const allValues = [
    ...data.flatMap((d) => [d.expected, d.actual]),
    ...(comparisonData?.flatMap((d) => [d.expected, d.actual]) || []),
  ];
  const maxValue = Math.max(...allValues, 1);

  return (
    <div
      style={{
        padding: `${outerPadding}px ${outerPadding}px ${outerPadding}px 0`,
      }}
    >
      {/* Subtitle */}
      {subtitle && (
        <Text
          strong
          style={{
            display: 'block',
            textAlign: 'center',
            marginBottom: isCompact ? 4 : Math.round(8 * scaleFactor),
            fontSize: isCompact ? 13 : 15,
            color: '#cedbef',
          }}
        >
          {subtitle}
        </Text>
      )}

      {/* Pending donations notice - always show in split mode */}
      {(pendingDonations > 0 || isSplitMode) && (
        <Text
          style={{
            display: 'block',
            textAlign: 'center',
            fontSize: isCompact ? 10 : 12,
            marginBottom: isCompact ? 4 : 8,
            fontStyle: 'italic',
            color: '#eaf3fc',
          }}
        >
          -- {pendingDonations} Donation{pendingDonations !== 1 ? 's' : ''}{' '}
          pending processing are not included --
        </Text>
      )}

      {/* Legend - centered below subtitle */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: legendGap,
          marginBottom: legendMarginBottom,
          fontSize: 12,
          color: '#cedbef',
        }}
      >
        {comparisonData && currentDateRangeLabel && comparisonDateRangeLabel ? (
          <>
            {/* Current Expected - light colored squares */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {LEGEND_COLORS.map(([expectedColor], idx) => (
                <span
                  key={`cur-exp-${idx}`}
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: expectedColor,
                    borderRadius: 2,
                  }}
                />
              ))}
              <span style={{ marginLeft: 2 }}>
                {t('legend.currentExp', 'Current Exp')} ({currentDateRangeLabel}
                )
              </span>
            </span>
            {/* Current Actual - dark colored squares */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {LEGEND_COLORS.map(([, actualColor], idx) => (
                <span
                  key={`cur-act-${idx}`}
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: actualColor,
                    borderRadius: 2,
                  }}
                />
              ))}
              <span style={{ marginLeft: 2 }}>
                {t('legend.currentAct', 'Current Act')} ({currentDateRangeLabel}
                )
              </span>
            </span>
            {/* Comparison date range with grey square */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  backgroundColor: '#a7b4c2',
                  borderRadius: 2,
                }}
              />
              <span style={{ marginLeft: 2, color: '#a7b4c2' }}>
                {t('legend.comparison', 'Comparison')} (
                {comparisonDateRangeLabel})
              </span>
            </span>
          </>
        ) : (
          <>
            {/* Standard legend without date range labels */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* Expected colors - light shades */}
              {LEGEND_COLORS.map(([expectedColor], idx) => (
                <span
                  key={`exp-${idx}`}
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: expectedColor,
                    borderRadius: 2,
                  }}
                />
              ))}
              <span style={{ marginLeft: 2 }}>
                {t('charts.expected', 'Expected')}
              </span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* Actual colors - dark shades */}
              {LEGEND_COLORS.map(([, actualColor], idx) => (
                <span
                  key={`act-${idx}`}
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: actualColor,
                    borderRadius: 2,
                  }}
                />
              ))}
              <span style={{ marginLeft: 2 }}>
                {t('charts.actual', 'Actual')}
              </span>
            </span>
          </>
        )}
      </div>

      {/* Bars */}
      {data.map((item, index) => {
        const [expectedColor, actualColor] = getCategoryColors(item.category);
        const expectedPercent = (item.expected / maxValue) * 100;
        const actualPercent = (item.actual / maxValue) * 100;
        const isSubRow = SUB_CATEGORIES.has(item.category);

        // Get comparison data for this category
        const compItem = comparisonData?.find(
          (c) => c.category === item.category
        );
        const compExpectedPercent = compItem
          ? (compItem.expected / maxValue) * 100
          : 0;
        const compActualPercent = compItem
          ? (compItem.actual / maxValue) * 100
          : 0;

        // Get previous period data for this category (trend arrows)
        const prevItem = previousPeriodData?.find(
          (p) => p.category === item.category
        );

        // Calculate success percentages for delta
        const successPercent =
          item.expected > 0 ? (item.actual / item.expected) * 100 : 0;
        const compSuccessPercent =
          compItem && compItem.expected > 0
            ? (compItem.actual / compItem.expected) * 100
            : undefined;
        const prevSuccessPercent =
          prevItem && prevItem.expected > 0
            ? (prevItem.actual / prevItem.expected) * 100
            : undefined;

        // Sub-rows use smaller spacing and bar heights
        const barHeight = isSubRow
          ? isCompact
            ? 12
            : 18
          : isCompact
          ? 16
          : 25;

        // Normal mode (no comparison): use flexbox layout
        // Label=1 | Bars(with values)=5 | Trend=2 | Ring=2, spanning 95%
        const isNormalMode = !compItem;

        return (
          <React.Fragment key={item.category}>
            {isNormalMode ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '95%',
                  gap: isCompact ? 4 : 8,
                  marginBottom: isSubRow
                    ? isCompact
                      ? 8
                      : 12
                    : isCompact
                    ? 14
                    : Math.max(12, Math.round(24 * scaleFactor)),
                }}
              >
                {/* Label — flex: 1 */}
                <div style={{ flex: 1, textAlign: 'right', minWidth: 0 }}>
                  <Text
                    strong={!isSubRow}
                    style={{
                      fontSize: isSubRow
                        ? isCompact
                          ? 9
                          : 11
                        : isCompact
                        ? 11
                        : 13,
                      color: '#fff',
                      fontWeight: isSubRow ? undefined : 700,
                      fontStyle: isSubRow ? 'italic' : undefined,
                    }}
                  >
                    {item.category}
                  </Text>
                </div>
                {/* Bars + values — flex: 6 */}
                <div style={{ flex: 6, minWidth: 0 }}>
                  {/* Expected bar */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: isCompact ? 2 : 4,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        position: 'relative',
                        height: barHeight,
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        boxShadow:
                          '0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
                        borderRadius: 3,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${expectedPercent}%`,
                          backgroundColor: expectedColor,
                          borderRadius: 3,
                        }}
                      />
                    </div>
                    <Text
                      style={{
                        color: getExpectedTextColor(item.category),
                        fontSize: isSubRow
                          ? isCompact
                            ? 11
                            : 14
                          : isCompact
                          ? 14
                          : 20,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.expected.toLocaleString()}
                    </Text>
                  </div>
                  {/* Actual bar */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        position: 'relative',
                        height: barHeight,
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        boxShadow:
                          '0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
                        borderRadius: 3,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${actualPercent}%`,
                          backgroundColor: actualColor,
                          borderRadius: 3,
                        }}
                      />
                    </div>
                    <Text
                      strong={!isSubRow}
                      style={{
                        color: getActualTextColor(item.category),
                        fontSize: isSubRow
                          ? isCompact
                            ? 11
                            : 14
                          : isCompact
                          ? 14
                          : 20,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.actual.toLocaleString()}
                    </Text>
                    {prevItem && (
                      <DeltaIndicator
                        current={item.actual}
                        comparison={prevItem.actual}
                        higherIsBetter
                        showZero
                        size="small"
                        darkBackground
                      />
                    )}
                  </div>
                </div>
                {/* Ring chart + Trend indicator — flex: 1, stacked vertically */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 0,
                    paddingLeft: isCompact ? 20 : 24,
                  }}
                >
                  {item.expected > 0 ? (
                    <>
                      {(() => {
                        const size = isSubRow
                          ? isCompact
                            ? 28
                            : 40
                          : isCompact
                          ? 36
                          : 54;
                        const strokeWidth = isSubRow ? 2 : isCompact ? 2 : 3;
                        const radius = (size - strokeWidth) / 2;
                        const circumference = 2 * Math.PI * radius;
                        const progressOffset =
                          circumference -
                          (Math.min(successPercent, 100) / 100) * circumference;

                        return (
                          <div
                            style={{
                              position: 'relative',
                              width: size,
                              height: size,
                              filter: getDropShadowFilter(),
                            }}
                          >
                            <svg
                              width={size}
                              height={size}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                transform: 'rotate(-90deg)',
                              }}
                            >
                              <circle
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill={expectedColor}
                                stroke="transparent"
                                strokeWidth={strokeWidth}
                              />
                              <circle
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill="none"
                                stroke={actualColor}
                                strokeWidth={strokeWidth}
                                strokeDasharray={circumference}
                                strokeDashoffset={progressOffset}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: size,
                                height: size,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Text
                                strong
                                style={{
                                  fontSize: isSubRow
                                    ? isCompact
                                      ? 8
                                      : 10
                                    : isCompact
                                    ? 10
                                    : 13,
                                  color: actualColor,
                                }}
                              >
                                {Math.round(successPercent)}%
                              </Text>
                            </div>
                          </div>
                        );
                      })()}
                      {prevSuccessPercent !== undefined && (
                        <div style={{ marginTop: 4 }}>
                          <DeltaIndicator
                            current={successPercent}
                            comparison={prevSuccessPercent}
                            higherIsBetter
                            showZero
                            size="small"
                            darkBackground
                            isPercentage
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <Text
                      style={{
                        fontSize: isCompact ? 12 : 16,
                        color: '#a7b4c2',
                      }}
                    >
                      -
                    </Text>
                  )}
                </div>
              </div>
            ) : (
              /* Comparison/inline mode: keep existing Row/Col layout */
              <Row
                gutter={[rowGutter, Math.round(rowGutter / 2)]}
                align="middle"
                style={{
                  marginBottom: isSubRow
                    ? isCompact
                      ? 8
                      : 12
                    : isCompact
                    ? 20
                    : Math.max(16, Math.round(32 * scaleFactor)),
                }}
              >
                <Col
                  style={{
                    width: labelColWidth,
                    flexShrink: 0,
                    textAlign: 'right',
                  }}
                >
                  <Text
                    strong={!isSubRow}
                    style={{
                      fontSize: isSubRow
                        ? isCompact
                          ? 9
                          : 11
                        : isCompact
                        ? 11
                        : 13,
                      color: '#fff',
                      fontWeight: isSubRow ? undefined : 700,
                      fontStyle: isSubRow ? 'italic' : undefined,
                    }}
                  >
                    {item.category}
                  </Text>
                </Col>
                <Col
                  flex="auto"
                  style={maxBarWidth ? { maxWidth: maxBarWidth } : undefined}
                >
                  {/* Expected bar with comparison overlay */}
                  <div
                    style={{
                      marginBottom: isCompact ? 2 : 4,
                      position: 'relative',
                      height: barHeight,
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      boxShadow:
                        '0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
                      borderRadius: 3,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${expectedPercent}%`,
                        backgroundColor: expectedColor,
                        borderRadius: 3,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '25%',
                        width: `${compExpectedPercent}%`,
                        backgroundColor: 'rgba(0, 0, 0, 0.37)',
                        borderRadius: 2,
                      }}
                    />
                  </div>
                  {/* Actual bar with comparison overlay */}
                  <div
                    style={{
                      position: 'relative',
                      height: barHeight,
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      boxShadow:
                        '0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
                      borderRadius: 3,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${actualPercent}%`,
                        backgroundColor: actualColor,
                        borderRadius: 3,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '25%',
                        width: `${compActualPercent}%`,
                        backgroundColor: 'rgba(0, 0, 0, 0.37)',
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </Col>
                <Col
                  style={{
                    width: valuesColWidth,
                    flexShrink: 0,
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* Compare expected value above */}
                  {(() => {
                    const expectedLabel = `${comparisonDateRangeLabel} Expected:`;
                    const charWidth = isCompact ? 6 : 7.5;
                    const labelWidth =
                      Math.ceil(expectedLabel.length * charWidth) + 10;
                    return (
                      <div
                        style={{
                          fontSize: isCompact ? 11 : 13,
                          lineHeight: isCompact ? '14px' : '16px',
                          color: '#a7b4c2',
                          paddingBottom: isCompact ? 2 : 4,
                        }}
                      >
                        <span
                          style={{
                            color: '#8096ab',
                            display: 'inline-block',
                            width: labelWidth,
                            marginLeft: -labelWidth,
                            textAlign: 'right',
                            paddingRight: 4,
                          }}
                        >
                          {expectedLabel}
                        </span>
                        {compItem.expected.toLocaleString()}
                      </div>
                    );
                  })()}
                  {/* Current expected value */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: isSubRow
                        ? isCompact
                          ? 11
                          : 14
                        : isCompact
                        ? 14
                        : 20,
                      lineHeight: isSubRow
                        ? isCompact
                          ? '14px'
                          : '20px'
                        : isCompact
                        ? '18px'
                        : '28px',
                    }}
                  >
                    <Text
                      style={{ color: getExpectedTextColor(item.category) }}
                    >
                      {item.expected.toLocaleString()}
                    </Text>
                    <DeltaIndicator
                      current={item.expected}
                      comparison={compItem.expected}
                      higherIsBetter
                      size="small"
                      darkBackground
                    />
                  </div>
                  {/* Current actual value */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: isSubRow
                        ? isCompact
                          ? 11
                          : 14
                        : isCompact
                        ? 14
                        : 20,
                      lineHeight: isSubRow
                        ? isCompact
                          ? '14px'
                          : '20px'
                        : isCompact
                        ? '18px'
                        : '28px',
                    }}
                  >
                    <Text
                      strong={!isSubRow}
                      style={{ color: getActualTextColor(item.category) }}
                    >
                      {item.actual.toLocaleString()}
                    </Text>
                    <DeltaIndicator
                      current={item.actual}
                      comparison={compItem.actual}
                      higherIsBetter
                      size="small"
                      darkBackground
                    />
                  </div>
                  {/* Compare actual value below */}
                  {(() => {
                    const actualLabel = `${comparisonDateRangeLabel} Actual:`;
                    const charWidth = isCompact ? 6 : 7.5;
                    const labelWidth =
                      Math.ceil(actualLabel.length * charWidth) + 10;
                    return (
                      <div
                        style={{
                          fontSize: isCompact ? 11 : 13,
                          lineHeight: isCompact ? '14px' : '16px',
                          color: '#a7b4c2',
                          paddingTop: isCompact ? 2 : 4,
                        }}
                      >
                        <span
                          style={{
                            color: '#8096ab',
                            display: 'inline-block',
                            width: labelWidth,
                            marginLeft: -labelWidth,
                            textAlign: 'right',
                            paddingRight: 4,
                          }}
                        >
                          {actualLabel}
                        </span>
                        {compItem.actual.toLocaleString()}
                      </div>
                    );
                  })()}
                </Col>
                <Col
                  style={{
                    width: circleColWidth,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isCompact ? 4 : Math.round(8 * scaleFactor),
                    marginLeft: circleMarginLeft,
                  }}
                >
                  {item.expected > 0 ? (
                    <>
                      {/* Current percentage circle */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        {(() => {
                          const size = isSubRow
                            ? isCompact
                              ? 28
                              : 40
                            : isCompact
                            ? 36
                            : 54;
                          const strokeWidth = isSubRow ? 2 : isCompact ? 2 : 3;
                          const radius = (size - strokeWidth) / 2;
                          const circumference = 2 * Math.PI * radius;
                          const progressOffset =
                            circumference -
                            (Math.min(successPercent, 100) / 100) *
                              circumference;

                          return (
                            <div
                              style={{
                                position: 'relative',
                                width: size,
                                height: size,
                                filter: getDropShadowFilter(),
                              }}
                            >
                              <svg
                                width={size}
                                height={size}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  transform: 'rotate(-90deg)',
                                }}
                              >
                                <circle
                                  cx={size / 2}
                                  cy={size / 2}
                                  r={radius}
                                  fill={expectedColor}
                                  stroke="transparent"
                                  strokeWidth={strokeWidth}
                                />
                                <circle
                                  cx={size / 2}
                                  cy={size / 2}
                                  r={radius}
                                  fill="none"
                                  stroke={actualColor}
                                  strokeWidth={strokeWidth}
                                  strokeDasharray={circumference}
                                  strokeDashoffset={progressOffset}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: size,
                                  height: size,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Text
                                  strong
                                  style={{
                                    fontSize: isSubRow
                                      ? isCompact
                                        ? 8
                                        : 10
                                      : isCompact
                                      ? 10
                                      : 13,
                                    color: actualColor,
                                  }}
                                >
                                  {Math.round(successPercent)}%
                                </Text>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      {/* Comparison percentage circle with delta below */}
                      {compSuccessPercent !== undefined && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: isCompact ? 36 : 54,
                            position: 'relative',
                          }}
                        >
                          {(() => {
                            const size = isCompact ? 25 : 38;
                            const strokeWidth = 2;
                            const radius = (size - strokeWidth) / 2;
                            const circumference = 2 * Math.PI * radius;
                            const progressOffset =
                              circumference -
                              (Math.min(compSuccessPercent, 100) / 100) *
                                circumference;

                            return (
                              <div
                                style={{
                                  position: 'relative',
                                  width: size,
                                  height: size,
                                  filter: getDropShadowFilter(),
                                }}
                              >
                                <svg
                                  width={size}
                                  height={size}
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    transform: 'rotate(-90deg)',
                                  }}
                                >
                                  <circle
                                    cx={size / 2}
                                    cy={size / 2}
                                    r={radius}
                                    fill="rgba(255,255,255,0.15)"
                                    stroke="transparent"
                                    strokeWidth={strokeWidth}
                                  />
                                  <circle
                                    cx={size / 2}
                                    cy={size / 2}
                                    r={radius}
                                    fill="none"
                                    stroke="#a7b4c2"
                                    strokeWidth={strokeWidth}
                                    strokeDasharray={circumference}
                                    strokeDashoffset={progressOffset}
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: size,
                                    height: size,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: isCompact ? 7 : 9,
                                      color: '#fff',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {Math.round(compSuccessPercent)}%
                                  </Text>
                                </div>
                              </div>
                            );
                          })()}
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-70%)',
                              marginTop: -5,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <DeltaIndicator
                              current={successPercent}
                              comparison={compSuccessPercent}
                              higherIsBetter
                              size="small"
                              darkBackground
                              isPercentage
                            />
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Text
                      style={{
                        fontSize: isCompact ? 12 : 16,
                        color: '#a7b4c2',
                      }}
                    >
                      -
                    </Text>
                  )}
                </Col>
              </Row>
            )}
            {/* Divider between bar sets in inline/comparison mode */}
            {comparisonData && index < data.length - 1 && (
              <Divider
                style={{
                  margin: '0 0 20px 0',
                  borderColor: 'rgba(255,255,255,0.2)',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ExpectedVsActualChart;
